import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, Download, Search, Package } from "lucide-react";
import { toast } from "sonner";

// Session Z52 — Admin "Signed agreements" vault.
export default function SignedAgreements() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [propertyFilter, setPropertyFilter] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [downloading, setDownloading] = useState(false);
  const [downloadingOne, setDownloadingOne] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (propertyFilter) params.set("property_id", propertyFilter);
      const { data } = await api.get(`/journeys/signed-agreements?${params.toString()}`);
      setRows(data?.items || []);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const properties = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => { if (r.property_id) map.set(r.property_id, r.property_name); });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [rows]);

  const toggleRow = (jid) => {
    const next = new Set(selected);
    if (next.has(jid)) next.delete(jid); else next.add(jid);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.journey_id)));
  };

  const downloadOne = async (row) => {
    setDownloadingOne(row.journey_id);
    try {
      const res = await api.get(
        `/journeys/${row.journey_id}/signed-agreement`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = row.agreement.original_filename || "signed-agreement.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiError(err, "Download failed"));
    } finally {
      setDownloadingOne(null);
    }
  };

  const bulkDownload = async () => {
    if (selected.size === 0) {
      toast.error("Select at least one row");
      return;
    }
    setDownloading(true);
    try {
      const res = await api.post(
        "/journeys/signed-agreements/bulk-download",
        { journey_ids: Array.from(selected) },
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `signed-agreements-${selected.size}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${selected.size} agreement${selected.size === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(apiError(err, "Bulk download failed"));
    } finally {
      setDownloading(false);
    }
  };

  // Session Z53 — CSV export of whatever's currently shown so legal/finance
  // can audit without downloading every PDF. Honours the active filters by
  // operating on the rows already in state.
  const exportCsv = () => {
    const headers = [
      "Journey ID", "Property", "Guest name", "Guest email", "Signed name",
      "Signed at", "Signed IP", "Check-in", "Check-out",
      "Agreement file", "Source file", "Version", "Stamped",
    ];
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.map(esc).join(",")];
    rows.forEach((r) => {
      lines.push([
        r.journey_id,
        r.property_name,
        r.guest_name,
        r.guest_email,
        r.signed_name,
        r.signed_at,
        r.signed_ip,
        r.check_in,
        r.check_out,
        r.agreement.original_filename,
        r.agreement.source_filename,
        r.agreement.version,
        r.agreement.stamped ? "yes" : "no",
      ].map(esc).join(","));
    });
    const csv = "\ufeff" + lines.join("\n");  // BOM so Excel reads UTF-8
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    const now = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `signed-agreements-${now}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} row${rows.length === 1 ? "" : "s"}`);
  };

  return (
    <Layout
      title="Signed agreements"
      subtitle="Every stamped rental agreement across your portfolio"
      actions={
        <div className="flex items-center gap-2">
          <Button
            data-testid="signed-vault-csv-export"
            onClick={() => exportCsv()}
            disabled={rows.length === 0}
            variant="outline"
            className="rounded-full px-5 inline-flex items-center gap-2 border-slate-200 bg-white"
          >
            <FileText className="w-4 h-4" />
            Export CSV
          </Button>
          <Button
            data-testid="signed-vault-bulk-download"
            onClick={bulkDownload}
            disabled={downloading || selected.size === 0}
            className="rounded-full ns-btn-primary px-5 inline-flex items-center gap-2"
          >
            <Package className="w-4 h-4" />
            {downloading
              ? "Zipping…"
              : selected.size > 0
                ? `Download ${selected.size} as ZIP`
                : "Download selected"}
          </Button>
        </div>
      }
    >
      <div className="ns-card p-5" data-testid="signed-vault-filters">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[16rem]">
            <div className="ns-label mb-1">Search</div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="signed-vault-search"
                className="pl-9 h-10 bg-white"
                placeholder="Guest name, email, property or filename"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              />
            </div>
          </div>
          <div className="min-w-[12rem]">
            <div className="ns-label mb-1">Property</div>
            <select
              data-testid="signed-vault-property-filter"
              value={propertyFilter}
              onChange={(e) => setPropertyFilter(e.target.value)}
              className="h-10 bg-white border border-slate-200 rounded-md px-3 text-sm w-full"
            >
              <option value="">All properties</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <Button
            data-testid="signed-vault-apply"
            onClick={load}
            className="rounded-full ns-btn-primary px-5"
          >
            Apply
          </Button>
        </div>
      </div>

      <div className="ns-card mt-6 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-slate-500">
            <tr>
              <th className="w-10 px-4 py-3 text-left">
                <input
                  type="checkbox"
                  data-testid="signed-vault-select-all"
                  disabled={rows.length === 0}
                  checked={rows.length > 0 && selected.size === rows.length}
                  onChange={toggleAll}
                  className="accent-teal-700"
                />
              </th>
              <th className="ns-label px-4 py-3 text-left">Guest</th>
              <th className="ns-label px-4 py-3 text-left">Property</th>
              <th className="ns-label px-4 py-3 text-left">Signed</th>
              <th className="ns-label px-4 py-3 text-left">Agreement</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="text-center py-12 text-slate-400 italic">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-16 text-slate-400 italic">
                  No signed agreements yet. Upload a rental-agreement PDF on a property and the next
                  guest signature will land here.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.journey_id}
                data-testid={`signed-vault-row-${r.journey_id}`}
                className="border-t border-slate-100 hover:bg-teal-50/20"
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    data-testid={`signed-vault-check-${r.journey_id}`}
                    checked={selected.has(r.journey_id)}
                    onChange={() => toggleRow(r.journey_id)}
                    className="accent-teal-700"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{r.signed_name || r.guest_name}</div>
                  <div className="text-[11px] text-slate-500">{r.guest_email || "—"}</div>
                </td>
                <td className="px-4 py-3 text-slate-600">{r.property_name || "—"}</td>
                <td className="px-4 py-3 text-slate-600">
                  <div className="text-xs">
                    {r.signed_at ? new Date(r.signed_at).toLocaleString("en-GB") : "—"}
                  </div>
                  {r.check_in && (
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      Stay: {r.check_in} → {r.check_out}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5 text-teal-700 shrink-0" />
                    <div className="min-w-0">
                      <div className="truncate text-xs text-slate-700">
                        {r.agreement.source_filename || r.agreement.original_filename || "agreement.pdf"}
                      </div>
                      <div className="text-[11px] text-slate-400">
                        v{r.agreement.version || 1}
                        {r.agreement.stamped ? " · stamped" : ""}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    data-testid={`signed-vault-download-${r.journey_id}`}
                    onClick={() => downloadOne(r)}
                    disabled={downloadingOne === r.journey_id}
                    className="text-slate-400 hover:text-teal-700 p-1.5"
                    title="Download signed PDF"
                  >
                    <Download className={`w-4 h-4 ${downloadingOne === r.journey_id ? "animate-pulse" : ""}`} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
